/**
 * Results of an IELTS sitting (D-105), laid out like the Test Report Form: the overall band and a
 * band per skill (half bands), the CLB and CEFR equivalents, the raw marks out of 40, the Writing
 * and Speaking criteria with their bands, then the correction of every question (answers,
 * explanations) and model answers. Always an estimate, never an official result.
 */

import { MarkNotes } from "../MarkNotes";
import { RecordingExpired } from "../RecordingExpired";
import { ApiError, useAcademyAttemptsRetrieve, useAcademyAttemptsReviewRetrieve } from "@agencyos/api-client";
import { type ExamPicture, type SpokenLine, hasPictures, parsePicture, parseSpokenLines } from "@agencyos/core";
import { Badge, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { ArrowLeft, CheckCircle2, Compass, Hourglass, Lightbulb, Route, Sprout, XCircle } from "lucide-react";
import { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { bandText } from "./band";
import { ExamPictureView, PictureOptions } from "../ExamPicture";
import { AudioReplay, ListeningReplay } from "../ListeningReplay";
import { SKILLS, type Skill, isObject, isSkill } from "../types";
import { CardTitle, PageHead, Ring, SectionTitle, SkillIcon } from "../ui";
import { AiUnavailable } from "../AiUnavailable";
import { unavailableSkills, useAcademyPaths } from "../paths";
import { useIeltsFormat } from "./CoursePage";
import { MethodLink } from "./MethodLink";
import { FeedbackInMyLanguage, InMyLanguage } from "./InMyLanguage";
import {
  type IeltsProduction,
  type IeltsResults as Results,
  type IeltsReviewQuestion,
  answerText,
  lowestClb,
  parseIeltsResults,
  parseIeltsReview,
  pick,
} from "@agencyos/core";

function waiting(results: Results): "all" | "some" | "none" {
  const shown = SKILLS.filter((s) => results.skills[s]);
  if (!shown.length && !results.placement) return "all";
  return shown.some((s) => results.skills[s]?.pending) ? "some" : "none";
}

function BandCards({ results, unavailable }: { results: Results; unavailable?: Set<string> }) {
  const { t } = useTranslation();
  const shown = SKILLS.filter((s) => results.skills[s]);
  return (
    <ul className="ac-scores">
      {shown.map((skill, index) => {
        const row = results.skills[skill];
        return (
          <li key={skill} className={`ac-score ac-skill--${skill} ao-enter`} style={{ "--ao-i": index } as CSSProperties}>
            <div className="ac-score__head">
              <SkillIcon skill={skill} size={16} />
              <h2 className="ac-headline">{t(`ielts.skill.${skill}`)}</h2>
            </div>
            {unavailable?.has(skill) ? (
              <AiUnavailable />
            ) : !row || row.pending || row.band === null ? (
              <p className="ac-status ac-secondary ac-score__pending">
                <Hourglass aria-hidden size={18} />
                {t("academy.results.grading")}
              </p>
            ) : (
              <>
                <div role="meter" aria-label={t("ielts.results.bandOf", { band: bandText(row.band) })} aria-valuemin={0} aria-valuemax={9} aria-valuenow={row.band}>
                  <Ring ratio={row.band / 9} size={132} stroke={11} tone={skill}>
                    <span className="ac-score__value ao-num">{bandText(row.band)}</span>
                    <span className="ac-score__max">{t("ielts.results.band")}</span>
                  </Ring>
                </div>
                <p className="ac-score__levels">
                  {row.clb !== null && <span className="ac-score__nclc">{t("ielts.results.clb", { n: row.clb })}</span>}
                  {row.cefr && <Badge>{t("academy.results.cefr", { level: row.cefr })}</Badge>}
                </p>
                {row.raw !== null && row.outOf !== null && (
                  <p className="ac-caption ao-num">
                    {row.outOf === 40
                      ? t("ielts.results.raw", { raw: row.raw })
                      : t("ielts.results.rawScaled", { raw: row.raw, outOf: row.outOf, raw40: row.raw40 ?? 0 })}
                  </p>
                )}
                {row.unanswered > 0 && (
                  <p className="ac-caption ao-num ko">{t("academy.results.unansweredCount", { count: row.unanswered, total: row.tasksPlanned })}</p>
                )}
                {Object.keys(row.tasks).length > 0 && (
                  <p className="ac-caption ao-num">
                    {Object.entries(row.tasks)
                      .map(([n, band]) => t(skill === "ee" ? "ielts.results.taskBand" : "ielts.results.partBand", { n, band: bandText(band) }))
                      .join(" · ")}
                  </p>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function OverallCard({ results }: { results: Results }) {
  const { t } = useTranslation();
  const clb = lowestClb(SKILLS.map((s) => results.skills[s]?.clb ?? null));
  if (results.overall === null) return null;
  return (
    <GlassCard as="section" className="ac-card ac-overall" aria-labelledby="ielts-overall">
      <Ring ratio={results.overall / 9} size={160} stroke={13} tone="accent">
        <span className="ac-overall__value ao-num">{bandText(results.overall)}</span>
        <span className="ac-caption">{t("ielts.results.overallShort")}</span>
      </Ring>
      <div className="ac-overall__text">
        <p className="ac-eyebrow">{t("ielts.results.estimateEyebrow")}</p>
        <h2 id="ielts-overall" className="ac-title-2">
          {t("ielts.results.overall", { band: bandText(results.overall) })}
        </h2>
        {clb !== null && <p className="ac-secondary">{t("ielts.results.clbLowest", { n: clb })}</p>}
        <p className="ac-footnote">{t("ielts.results.rounding")}</p>
      </div>
    </GlassCard>
  );
}

function responseText(q: IeltsReviewQuestion): string {
  if (q.response === null || q.response === "" || (Array.isArray(q.response) && q.response.every((v) => v === null || v === ""))) return "";
  return answerText(q, q.response);
}

function QuestionReview({ q }: { q: IeltsReviewQuestion }) {
  const { t } = useTranslation();
  const mine = responseText(q);
  return (
    <li className={`ac-review ac-surface${q.correct ? " ac-review--right" : " ac-review--wrong"}`}>
      <p className="ac-review__head">
        <span className="ac-review__mark" aria-hidden="true">
          {q.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </span>
        <strong className="ac-caption ao-num">
          {t("ielts.player.questionN", { n: q.number ?? "" })} · {t(`ielts.types.${q.type}`)} · {t("ielts.results.points", { points: q.points, marks: q.marks })}
        </strong>
        <span className="visually-hidden">{q.correct ? t("academy.player.right") : t("academy.results.wrongShort")}</span>
      </p>
      <ExamPictureView picture={q.image} lang="en" size="medium" />
      <p className="ac-review__stem" lang="en">
        {q.prompt}
      </p>
      {hasPictures(q.optionImages) && (
        <PictureOptions
          name={`ir-${q.id}`}
          options={q.options}
          images={q.optionImages}
          answer={typeof q.accepted === "number" ? q.accepted : null}
          given={typeof q.response === "number" ? q.response : null}
          showText
          lang="en"
        />
      )}
      <dl className="ac-review__answers">
        <div>
          <dt>{t("ielts.results.yours")}</dt>
          <dd lang="en">{mine || t("academy.results.noAnswer")}</dd>
        </div>
        <div>
          <dt>{t("ielts.results.expected")}</dt>
          <dd lang="en">{answerText(q, q.accepted)}</dd>
        </div>
      </dl>
      {q.explanation ? (
        <>
          <p className="ac-review__why" lang="en">
            {q.explanation}
          </p>
          <InMyLanguage text={q.explanation} source="en" examLanguage="en" kind="correction" />
        </>
      ) : (
        <p className="ac-review__why ac-review__why--pending">{t("listening.review.explanationSoon")}</p>
      )}
      {!q.correct && q.skill && <MethodLink exam="ielts_general" skill={q.skill} type={q.type} />}
    </li>
  );
}

function ProductionReview({ production, labelOf }: { production: IeltsProduction; labelOf: (skill: string, key: string) => string }) {
  const { t } = useTranslation();
  const { grade } = production;
  const skill: Skill = isSkill(production.skill) ? production.skill : "ee";
  return (
    <article className="ac-production">
      <h3 className="ac-title-3">
        {t(`ielts.skill.${skill}`)} · {t(skill === "ee" ? "ielts.writing.task" : "ielts.speaking.partN", { n: production.task })}
        {grade.band !== null && ` · ${t("ielts.results.bandOf", { band: bandText(grade.band) })}`}
      </h3>
      <p className="ac-prompt ac-prompt--small" lang="en">
        {production.prompt}
      </p>
      {production.text && (
        <details>
          <summary>{skill === "eo" ? t("academy.results.transcript") : t("academy.results.yourText")}</summary>
          <p className="ac-production-text" lang="en">
            {production.text}
          </p>
          {skill === "ee" && <p className="ac-footnote">{t("academy.writing.wordsPlain", { count: production.wordCount })}</p>}
        </details>
      )}
      {skill === "eo" && production.turns.length > 0 && (
        <details>
          <summary>{t("ielts.results.conversation")}</summary>
          <ul className="ac-transcript">
            {production.turns.map((turn, index) => (
              <li key={index} className={`ac-bubble ac-bubble--${turn.role === "examiner" ? "them" : "me"}`}>
                <strong className="ac-bubble__who">{t(`ielts.speaking.role.${turn.role === "examiner" ? "examiner" : "learner"}`)}</strong> <span lang="en">{turn.text}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {skill === "eo" && production.audioUrl && (
        <div className="ac-listen-again">
          <p className="ac-label">{t("academy.results.listenAgain")}</p>
          <AudioReplay url={production.audioUrl} label={t("academy.results.listenAgain")} />
        </div>
      )}
      {production.audioExpired && <RecordingExpired />}
      {production.unanswered && <p className="ac-status ko">{t("academy.results.unanswered")}</p>}
      <MarkNotes grade={production.rawGrade} productionId={production.id} />
      {(production.status === "to_trainer" || production.status === "submitted") && (
        <p className="ac-status ac-secondary">
          <Hourglass aria-hidden size={18} />
          {production.status === "to_trainer" ? t("academy.results.toTrainer") : t("academy.results.grading")}
        </p>
      )}
      {grade.criteria.length > 0 && (
        <table className="ac-criteria">
          <caption className="visually-hidden">{t("academy.results.criteria")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("academy.results.criterion")}</th>
              <th scope="col" className="num">
                {t("ielts.results.bandCol")}
              </th>
            </tr>
          </thead>
          <tbody>
            {grade.criteria.map((c) => (
              <tr key={c.key}>
                <td>
                  <strong>{labelOf(skill, c.key)}</strong>
                  {c.comment && <p className="ac-footnote">{c.comment}</p>}
                  <span className="ac-bar" aria-hidden="true">
                    <span style={{ transform: `scaleX(${Math.min(1, (c.band ?? 0) / 9)})` }} />
                  </span>
                </td>
                <td className="num ao-num">{bandText(c.band)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {grade.comment && <p className="ac-comment">{grade.comment}</p>}
      <FeedbackInMyLanguage comment={grade.comment} errors={grade.errors} examLanguage="en" />
      {grade.errors.length > 0 && (
        <div className="ac-block">
          <h4 className="ac-label">{t("academy.results.errors")}</h4>
          <ul className="ac-errors">
            {grade.errors.map((e, index) => (
              <li key={index} className="ac-error-row">
                <p lang="en">
                  <del>{e.excerpt}</del> → <ins>{e.correction}</ins>
                </p>
                {e.explanation && <p className="ac-footnote">{e.explanation}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {grade.corrected && (
        <details>
          <summary>{t("academy.results.corrected")}</summary>
          <p className="ac-production-text" lang="en">
            {grade.corrected}
          </p>
        </details>
      )}
      {grade.model && (
        <details>
          <summary>{t("academy.results.model")}</summary>
          <p className="ac-production-text" lang="en">
            {grade.model}
          </p>
        </details>
      )}
      {grade.advice.length > 0 && (
        <div className="ac-block ac-advice">
          <h4 className="ac-label">
            <Lightbulb aria-hidden size={14} /> {t("academy.results.advice")}
          </h4>
          <ul>
            {grade.advice.map((a, index) => (
              <li key={index}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

/** The Listening parts of the review (D-106): audio to hear again and transcript. */
function listeningParts(value: unknown): { part: number; audioUrl: string | null; transcript: SpokenLine[]; image: ExamPicture | null }[] {
  const rows = isObject(value) && Array.isArray(value.listening) ? value.listening : [];
  return rows.filter(isObject).map((row) => ({
    part: typeof row.part === "number" ? row.part : 0,
    audioUrl: typeof row.audio_url === "string" && row.audio_url ? row.audio_url : null,
    transcript: parseSpokenLines(row.transcript),
    image: parsePicture(row.image),
  }));
}

export function IeltsResults({ id }: { id: string }) {
  const paths = useAcademyPaths();
  const { t, i18n } = useTranslation();
  const attempt = useAcademyAttemptsRetrieve(id, { query: { retry: false } });
  const raw: unknown = attempt.data?.status === 200 ? attempt.data.data : undefined;
  const status = raw && typeof raw === "object" && "status" in raw ? String((raw as { status: unknown }).status) : "";
  const stateResults = raw && typeof raw === "object" && "results" in raw ? parseIeltsResults((raw as { results: unknown }).results) : null;
  const inProgress = status === "in_progress";
  const review = useAcademyAttemptsReviewRetrieve(id, {
    query: {
      enabled: Boolean(raw) && !inProgress,
      retry: false,
      refetchInterval: (query) => {
        const data = query.state.data?.status === 200 ? parseIeltsReview(query.state.data.data) : null;
        const wait = data ? waiting(data.results) : "all";
        return wait === "all" ? 4000 : wait === "some" ? 20000 : false;
      },
    },
  });
  const format = useIeltsFormat();
  const data = useMemo(() => (review.data?.status === 200 ? parseIeltsReview(review.data.data) : null), [review.data]);
  const labelOf = (skill: string, key: string) => {
    const row = (format.rubrics[skill] ?? []).find((r) => r.key === key);
    return row ? pick(row.label, i18n.language) : key;
  };

  const back = (
    <Link to={paths.home} className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {t("academy.results.back")}
    </Link>
  );
  if (attempt.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (attempt.error instanceof ApiError && attempt.error.status === 404) {
    return (
      <div className="ac-page">
        {back}
        <p className="ac-secondary">{t("academy.results.notFound")}</p>
      </div>
    );
  }
  if (inProgress) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p>{t("academy.results.notFinished")}</p>
        <Link className={buttonClass()} to={paths.attempt(id)}>
          {t("academy.history.resume")}
        </Link>
      </div>
    );
  }

  const results = data?.results ?? stateResults ?? { skills: {}, overall: null, placement: null };
  const placement = results.placement;
  if (placement) {
    return (
      <div className="ac-page ac-results">
        {back}
        <PageHead eyebrow={t("ielts.placement.title")} title={t("ielts.placement.resultTitle")} subtitle={<p className="ac-note">{t("ielts.estimateLong")}</p>} />
        <GlassCard as="section" className={`ac-card ac-overall${placement.beginner ? " ac-card--success" : ""}`} aria-labelledby="ielts-placement">
          <Ring ratio={(placement.band ?? 0) / 9} size={160} stroke={13} tone="accent">
            <span className="ac-overall__value ao-num">{bandText(placement.band)}</span>
            <span className="ac-caption">{t("ielts.results.band")}</span>
          </Ring>
          <div className="ac-overall__text">
            <CardTitle id="ielts-placement" icon={placement.beginner ? <Sprout size={16} /> : <Compass size={16} />} tone={placement.beginner ? "success" : "info"}>
              {placement.beginner ? t("ielts.placement.beginnerTitle") : t("ielts.placement.levelTitle", { level: t(`ielts.levels.${placement.level}`, { defaultValue: placement.level }) })}
            </CardTitle>
            <p className="ac-secondary">
              {placement.beginner ? t("ielts.placement.beginnerText") : t("ielts.placement.levelText", { cefr: placement.cefr, band: bandText(placement.band) })}
            </p>
            <p className="ac-caption ao-num">{t("ielts.placement.answered", { right: placement.right, total: placement.answered })}</p>
            <div className="ac-card__actions ac-card__actions--start">
              <Link className={buttonClass("primary")} to="/academy/course">
                <Route aria-hidden size={16} />
                {t("ielts.placement.toCourse")}
              </Link>
            </div>
          </div>
        </GlassCard>
      </div>
    );
  }

  const wait = waiting(results);
  const questions = data?.questions ?? [];
  const productions = data?.productions ?? [];
  const bySkill = (skill: Skill) => questions.filter((q) => q.skill === skill);
  const parts = listeningParts(review.data?.status === 200 ? review.data.data : null);

  return (
    <div className="ac-page ac-results">
      {back}
      <PageHead
        eyebrow={t("ielts.results.eyebrow")}
        title={t("ielts.results.title")}
        subtitle={
          <p className="ac-note" role="note">
            {t("ielts.estimateLong")}
          </p>
        }
      />
      {(review.isLoading || wait === "all") && (
        <div className="ac-surface ac-card ac-card--center ac-preparing">
          <p className="ac-status" role="status">
            {t("academy.results.preparing")}
          </p>
        </div>
      )}
      {wait !== "all" && <OverallCard results={results} />}
      {wait !== "all" && <BandCards results={results} unavailable={unavailableSkills(raw)} />}

      {productions.length > 0 && (
        <section className="ac-section" aria-labelledby="ielts-productions">
          <SectionTitle id="ielts-productions">{t("ielts.results.productions")}</SectionTitle>
          <div className="ac-stack-cards">
            {productions.map((p) => (
              <GlassCard key={`${p.skill}-${p.task}`} as="div" className="ac-card">
                <ProductionReview production={p} labelOf={labelOf} />
              </GlassCard>
            ))}
          </div>
        </section>
      )}

      {(["co", "ce"] as Skill[]).map((skill) =>
        bySkill(skill).length ? (
          <section key={skill} className="ac-section" aria-labelledby={`ielts-review-${skill}`}>
            <SectionTitle id={`ielts-review-${skill}`}>{t("ielts.results.review", { skill: t(`ielts.skill.${skill}`) })}</SectionTitle>
            {skill === "co" &&
              parts.map((part) => (
                <div key={part.part} className="ac-replay-part">
                  <h3 className="ac-headline">{t("listening.review.part", { n: part.part })}</h3>
                  <ExamPictureView picture={part.image} lang="en" size="medium" />
                  <ListeningReplay audioUrl={part.audioUrl} lines={part.transcript} lang="en" preferred="en-GB" />
                </div>
              ))}
            <ol className="ac-review-list">
              {bySkill(skill).map((q, index) => (
                <QuestionReview key={`${q.id}-${index}`} q={q} />
              ))}
            </ol>
          </section>
        ) : null,
      )}
    </div>
  );
}
